import { PlaylistLesson } from '../catalog.types';

export interface TextSegment {
  text: string;
  match: boolean;
}

export interface LessonSearchHit {
  lesson: PlaylistLesson;
  titleSegments: TextSegment[];
  snippetSegments: TextSegment[];
}

const SNIPPET_LIMIT = 160;
const SNIPPET_LEAD = 40;

/** Lowercase + `ё`→`е`, index-safe: every source char keeps its slot. */
function normalize(value: string): string {
  return Array.from(value, char => {
    const lower = char.toLowerCase();
    return (lower.length === char.length ? lower : char).replace(/ё/g, 'е');
  }).join('');
}

function segmentsOf(text: string, normalized: string, needle: string, from: number, to: number): TextSegment[] {
  const out: TextSegment[] = [];
  let cursor = from;
  for (let at = normalized.indexOf(needle, from); at >= 0 && at + needle.length <= to; at = normalized.indexOf(needle, cursor)) {
    if (at > cursor) out.push({ text: text.slice(cursor, at), match: false });
    out.push({ text: text.slice(at, at + needle.length), match: true });
    cursor = at + needle.length;
  }

  if (cursor < to) out.push({ text: text.slice(cursor, to), match: false });

  return out;
}

/** `null` = empty/whitespace query (sections stay). Otherwise the flat hit list, possibly empty. */
export function searchLessons(lessons: readonly PlaylistLesson[], query: string): LessonSearchHit[] | null {
  const needle = normalize(query.trim());
  if (!needle) return null;

  const hits: LessonSearchHit[] = [];
  for (const lesson of lessons) {
    const { title, description } = lesson;
    const normalizedTitle = normalize(title);
    const normalizedDescription = normalize(description);
    const descriptionAt = normalizedDescription.indexOf(needle);
    if (normalizedTitle.indexOf(needle) < 0 && descriptionAt < 0) continue;

    const short = description.length <= SNIPPET_LIMIT;
    const start = short || descriptionAt < 0 ? 0 : Math.max(0, descriptionAt - SNIPPET_LEAD);
    const end = Math.min(description.length, Math.max(start + SNIPPET_LIMIT, descriptionAt + needle.length));
    hits.push({
      lesson,
      titleSegments: segmentsOf(title, normalizedTitle, needle, 0, title.length),
      snippetSegments: segmentsOf(description, normalizedDescription, needle, start, end),
    });
  }

  return hits;
}
